import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Checkbox,
  Paper,
  Grid,
  TextField,
  InputAdornment,
  Autocomplete,
  Chip,
  Card,
  CardContent,
  Alert
} from '@mui/material';
import axios from '../../../../config/axiosConfig';

// Icons
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LockIcon from '@mui/icons-material/Lock';

const MarketEventConfig = ({ alertData, onUpdateAlertData }) => {
  // State for stock search
  const [stockSearchTerm, setStockSearchTerm] = useState('');
  const [stockSearchResults, setStockSearchResults] = useState([]);
  const [isSearchingStocks, setIsSearchingStocks] = useState(false);
  const [selectedStock, setSelectedStock] = useState(null);
  
  // Get the conditions from alert data
  const conditions = alertData.conditions || {};
  const mainFilters = alertData.mainFilters || [];
  
  // Event types
  const eventTypes = [
    { 
      id: 'earnings', 
      label: 'Daily Earnings Announcements', 
      description: 'Get a daily summary of companies reporting earnings',
      icon: <AttachMoneyIcon />,
      special: true,
      available: true
    },
    { 
      id: 'dividends', 
      label: 'Dividend Announcements', 
      description: 'Get notified about dividend declarations',
      icon: <AttachMoneyIcon />,
      available: false
    },
    { 
      id: 'splits', 
      label: 'Stock Splits', 
      description: 'Get notified about upcoming stock splits',
      icon: <ShowChartIcon />,
      available: false
    },
    { 
      id: 'ipo', 
      label: 'IPO Announcements', 
      description: 'Get notified about upcoming IPOs',
      icon: <NewReleasesIcon />,
      available: false
    },
    { 
      id: 'economic', 
      label: 'Economic Data Releases', 
      description: 'Get notified about major economic data releases',
      icon: <CalendarTodayIcon />,
      available: false
    }
  ];
  
  // Extract symbols from main filters
  const symbols = mainFilters
    .filter(filter => filter.type === 'symbol')
    .map(filter => filter.value);
  
  // Get selected event types
  const selectedEventTypes = conditions.eventTypes || [];
  
  // Check if daily earnings is selected
  const isDailyEarningsSelected = selectedEventTypes.includes('earnings');
  
  // Debounced search for stocks
  useEffect(() => {
    if (!stockSearchTerm || stockSearchTerm.length < 2 || isDailyEarningsSelected) {
      setStockSearchResults([]);
      return;
    }
    
    const timer = setTimeout(() => {
      searchStocks(stockSearchTerm);
    }, 500);
    
    return () => clearTimeout(timer);
  }, [stockSearchTerm, isDailyEarningsSelected]);
  
  // Search for stocks using the API
  const searchStocks = async (term) => {
    setIsSearchingStocks(true);
    
    try {
      const response = await axios.get(`/api/search/stocks?query=${term}`);
      setStockSearchResults(response.data || []);
    } catch (error) {
      console.error('Error searching stocks:', error);
      setStockSearchResults([]);
    } finally {
      setIsSearchingStocks(false);
    }
  };
  
  // Handle event type toggle
  const handleEventTypeToggle = (eventType) => {
    // Check if event type is available
    const eventTypeObj = eventTypes.find(type => type.id === eventType);
    if (!eventTypeObj.available) {
      return; // Do nothing if not available
    }
    
    const currentTypes = conditions.eventTypes || [];
    let updatedTypes;
    
    if (currentTypes.includes(eventType)) {
      // Remove event type if already selected
      updatedTypes = currentTypes.filter(type => type !== eventType);
    } else {
      // Add event type if not selected
      updatedTypes = [...currentTypes, eventType];
    }
    
    // Clear symbols if daily earnings is selected
    let updatedMainFilters = [...mainFilters];
    if (eventType === 'earnings' && !currentTypes.includes('earnings')) {
      updatedMainFilters = mainFilters.filter(filter => filter.type !== 'symbol');
    }
    
    onUpdateAlertData({
      conditions: {
        ...conditions,
        eventTypes: updatedTypes
      },
      mainFilters: updatedMainFilters
    });
  };
  
  // Handle checkbox click (stopPropagation to prevent double-toggling)
  const handleCheckboxClick = (event, eventType) => {
    event.stopPropagation();
    
    // Check if event type is available
    const eventTypeObj = eventTypes.find(type => type.id === eventType);
    if (!eventTypeObj.available) {
      return; // Do nothing if not available
    }
    
    handleEventTypeToggle(eventType);
  };
  
  // Handle adding a symbol to the alert
  const handleAddSymbol = (symbol) => {
    if (!symbol || isDailyEarningsSelected) return;
    
    // Check if symbol already exists
    if (symbols.includes(symbol)) {
      return;
    }
    
    // Update main filters with the new symbol
    const updatedMainFilters = [
      ...mainFilters,
      { type: 'symbol', value: symbol }
    ];
    
    onUpdateAlertData({
      mainFilters: updatedMainFilters
    });
    
    // Clear the search
    setStockSearchTerm('');
    setSelectedStock(null);
  };
  
  // Handle removing a symbol
  const handleRemoveSymbol = (symbolToRemove) => {
    const updatedMainFilters = mainFilters.filter(
      filter => !(filter.type === 'symbol' && filter.value === symbolToRemove)
    );
    
    onUpdateAlertData({
      mainFilters: updatedMainFilters
    });
  };
  
  return (
    <Box sx={{ py: 2 }}>
      <Typography variant="h6" gutterBottom>
        Select Market Event Types
      </Typography>
      
      <Grid container spacing={3}>
        {eventTypes.map((eventType) => (
          <Grid item xs={12} sm={6} key={eventType.id}>
            <Card 
              variant="outlined" 
              sx={{
                display: 'flex',
                height: '100%',
                border: selectedEventTypes.includes(eventType.id) && eventType.available
                  ? '2px solid' 
                  : '1px solid',
                borderColor: selectedEventTypes.includes(eventType.id) && eventType.available
                  ? 'primary.main' 
                  : 'divider',
                cursor: eventType.available ? 'pointer' : 'default',
                transition: 'all 0.2s',
                '&:hover': {
                  borderColor: eventType.available ? 'primary.main' : 'divider',
                },
                position: 'relative',
                opacity: eventType.available ? 1 : 0.7
              }}
              onClick={() => eventType.available && handleEventTypeToggle(eventType.id)}
            >
              {!eventType.available && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    bottom: 0,
                    left: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.05)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1
                  }}
                >
                  <Box sx={{ textAlign: 'center' }}>
                    <LockIcon sx={{ fontSize: 24, opacity: 0.7, mb: 1 }} />
                  </Box>
                </Box>
              )}
              
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 2,
                  bgcolor: 'action.hover',
                  color: selectedEventTypes.includes(eventType.id) && eventType.available ? 'primary.main' : 'text.secondary',
                  zIndex: 2
                }}
              >
                {eventType.icon}
              </Box>
              
              <CardContent sx={{ flexGrow: 1, zIndex: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="subtitle1" component="div">
                    {eventType.label}
                  </Typography>
                  <Checkbox
                    checked={selectedEventTypes.includes(eventType.id) && eventType.available}
                    onChange={() => {}}
                    onClick={(e) => handleCheckboxClick(e, eventType.id)}
                    sx={{ ml: 'auto' }}
                    disabled={!eventType.available}
                  />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {eventType.description}
                </Typography>
                {eventType.special && selectedEventTypes.includes(eventType.id) && eventType.available && (
                  <Typography variant="caption" color="primary" sx={{ display: 'block', mt: 1 }}>
                    Special alert: No additional configuration needed
                  </Typography>
                )}
                {!eventType.available && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    This feature is coming soon
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      
      {/* Special handling for Daily Earnings */}
      {isDailyEarningsSelected && (
        <Paper variant="outlined" sx={{ p: 2, mt: 3 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Daily Earnings Alert will send you a summary of all companies reporting earnings each day.
              No additional configuration is required.
            </Typography>
          </Alert>
        </Paper>
      )}
      
      {/* Stock selection for non-earnings alerts */}
      {selectedEventTypes.length > 0 && !isDailyEarningsSelected && (
        <>
          <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
            Select Stocks (Optional)
          </Typography>
          
          <Paper variant="outlined" sx={{ p: 2, mb: 4 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              If you select specific stocks, you'll only receive alerts for these stocks.
              Otherwise, you'll receive alerts for all stocks based on the selected event types.
            </Typography>
            
            <Box sx={{ mb: 2 }}>
              <Autocomplete
                value={selectedStock}
                onChange={(event, newValue) => {
                  if (typeof newValue === 'string') {
                    handleAddSymbol(newValue);
                  } else if (newValue && newValue.symbol) {
                    handleAddSymbol(newValue.symbol);
                  }
                }}
                inputValue={stockSearchTerm}
                onInputChange={(event, newInputValue) => {
                  setStockSearchTerm(newInputValue);
                }}
                loading={isSearchingStocks}
                options={stockSearchResults}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') return option;
                  return `${option.symbol} - ${option.name || ''}`;
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search for stocks"
                    variant="outlined"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <ShowChartIcon />
                        </InputAdornment>
                      )
                    }}
                    helperText="Search by symbol or company name"
                  />
                )}
                freeSolo
                fullWidth
              />
            </Box>
            
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {symbols.length > 0 ? (
                symbols.map((symbol) => (
                  <Chip
                    key={symbol}
                    label={symbol}
                    onDelete={() => handleRemoveSymbol(symbol)}
                    color="primary"
                    variant="outlined"
                    icon={<ShowChartIcon />}
                  />
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No stocks selected. You'll receive alerts for all stocks matching your selected event types.
                </Typography>
              )}
            </Box>
          </Paper>
        </>
      )}
      
      {selectedEventTypes.length === 0 && (
        <Typography variant="body2" color="error" sx={{ mt: 2 }}>
          Please select at least one event type.
        </Typography>
      )}
    </Box>
  );
};

export default MarketEventConfig;